import * as React from "react";
import {
  Grid,
  Badge,
  Table,
  Select,
  Divider,
  useMantineTheme,
  Stack,
  Flex,
  Title,
  ScrollArea,
  Text,
} from "@mantine/core";
import { Link, useSearchParams } from "react-router-dom";

import {
  getConfirmationStatus,
  getSystemStatus,
  getColor,
} from "../UseCaseUtils";
import { styles } from "../../styles";
import { UseCaseBasicType, UseCaseRunDetails } from "@supportinitiative/common";

function getStats(
  type: any,
  reconId: string,
  stats: UseCaseRunDetails["stats"],
  useCase: UseCaseBasicType
) {
  const reconDefinition = useCase.definition.recons.find(
    (r) => (r.id = reconId)
  );

  if (!reconDefinition) {
    return {
      runId: "",
      runSeq: "",
      status: [],
      datasets: [],
      values: [],
    };
  }

  // get the first row. Ideally will get only 1 row per recon
  const reconStats = stats.find((stat) => stat.recon === reconId);

  if (!reconStats) {
    return {
      runId: "",
      runSeq: "",
      status: [],
      datasets: [],
      values: [],
    };
  }
  const finalStats: {
    runId: string;
    runSeq: string;
    status: string[]; // rows
    datasets: string[]; // columns
    values: string[][];
  } = {
    runId: reconStats.runId,
    runSeq: reconStats.runSeq,
    status: [],
    datasets: [],
    values: [],
  };

  const datasets = reconDefinition.definition.datasets
    .filter((d) => d.id !== "EVENT")
    .map((d) => `${d.name} \n [ ${d.id} ]`);

  const reconDatasets = (reconDefinition.definition.datasets || []).filter(
    (d) => d.id !== "EVENT"
  );

  if (type === "USER") {
    finalStats.status.push(...(useCase.definition.userStatus || []).sort());

    // Add defaults
    finalStats.status.push("__NONE");
    // finalStats.status.push("__ALL");

    finalStats.datasets.push(...datasets);

    for (const userStatus of finalStats.status || []) {
      let values = Array(finalStats.datasets.length).fill("0");

      let statusIndex = 0;
      for (const dataset of reconDatasets || []) {
        const datasetStat = reconStats.stats[dataset.id];

        // Only 1 row per status
        const statusStat = datasetStat.userStatus.find(
          (stat) => stat.status.toLowerCase() === userStatus.toLowerCase()
        );

        if (statusStat) {
          values[statusIndex] = `${statusStat.count}`;
        }
        statusIndex = statusIndex + 1;
      }

      finalStats.values.push(values);
    }
  } else if (type === "CONFIRMATION") {
    finalStats.status.push("CONFIRMED", "UNCONFIRMED");

    // Add defaults
    finalStats.status.push("__NONE");
    // finalStats.status.push("__ALL");

    finalStats.datasets.push(...datasets);

    for (const confirmationStatus of finalStats.status || []) {
      let values = Array(finalStats.datasets.length).fill("0");

      let statusIndex = 0;
      for (const dataset of reconDatasets || []) {
        const datasetStat = reconStats.stats[dataset.id];

        // Only 1 row per status
        const statusStat = datasetStat.confirmationStatus.find(
          (stat) =>
            stat.status.toLowerCase() === confirmationStatus.toLowerCase()
        );

        if (statusStat) {
          values[statusIndex] = `${statusStat.count}`;
        }
        statusIndex = statusIndex + 1;
      }

      finalStats.values.push(values);
    }
  }

  return finalStats;
}

function statusRows(
  entityId: string,
  id: string,
  recon: string,
  runId: string,
  runSeq: string,
  access: string,
  status: string,
  statusStats: {
    status: string[];
    datasets: string[];
    values: string[][];
  }
) {
  return Array.from({ length: statusStats.status.length }, (_, i) => (
    <Table.Tr key={statusStats.status[i]}>
      <Table.Td>
        <Link
          to={`/app/usecases/view/status?entityId=${entityId}&id=${id}&recon=${recon}&runId=${runId}&runSeq=${runSeq}&access=${access}&status=${status}&statusId=${statusStats.status[i]}`}
          style={styles.badgeLink}
        >
          <Text size="xs" fw={500}>
            {statusStats.status[i]}
          </Text>
        </Link>
      </Table.Td>
      {...Array.from({ length: statusStats.datasets.length }, (_, j) => (
        <Table.Td ta="end" key={j}>
          <Text size="xs" fw={500}>
            {statusStats.values[i][j]}
          </Text>
        </Table.Td>
      ))}
    </Table.Tr>
  ));
}

export default function Stats({
  useCase,
  run,
  access,
  stats,
}: {
  useCase: UseCaseBasicType;
  run: UseCaseRunDetails;
  access: string;
  stats: UseCaseRunDetails["stats"];
}) {
  const theme = useMantineTheme();

  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId");

  const recons = useCase.definition.recons.map((recon) => {
    return {
      label: recon.name,
      value: recon.id,
    };
  });

  const [recon, setRecon] = React.useState<any>(
    recons[0] ? recons[0].value : ""
  );

  const [statusType, setStatusType] = React.useState<any>("USER");

  const statusStats = getStats(statusType, recon, stats, useCase);

  return (
    <Stack mt="xs" h="100%" p="xs" bg="#FFEAEA">
      <Flex>
        <Title
          mt="xs"
          order={5}
          py={0}
          px={theme.spacing.sm}
          style={{
            borderLeft: `4px solid ${theme.colors.emerald[0]}`,
          }}
        >
          Stats
        </Title>
      </Flex>
      <Grid align="flex-start" gutter={"xs"}>
        <Grid.Col span={6}>
          <Select
            size="xs"
            data={recons}
            value={recon}
            onChange={(value) => {
              if (value) {
                setRecon(value);
              }
            }}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            size="xs"
            data={[
              { value: "USER", label: "User Status" },
              { value: "CONFIRMATION", label: "Confirmation Status" },
            ]}
            value={statusType}
            onChange={(value) => {
              if (value) {
                setStatusType(value);
              }
            }}
          />
        </Grid.Col>
      </Grid>
      <ScrollArea type="auto" offsetScrollbars scrollbarSize={16}>
        {statusStats ? (
          <Table verticalSpacing="sm" fs={"xs"}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th></Table.Th>
                {statusStats.datasets.map((dataset) => {
                  return (
                    <Table.Th
                      ta="end"
                      style={{ minWidth: "150px" }}
                      key={dataset}
                    >
                      <Text size="xs" fw={500}>
                        {dataset}
                      </Text>
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {statusRows(
                entityId as string,
                id as string,
                recon,
                run.details.runId,
                useCase.definition.preferences.run === "CONTINUOUS"
                  ? run.details.completedRunSeq
                  : run.details.runSeq,
                access,
                statusType,
                statusStats
              )}
            </Table.Tbody>
          </Table>
        ) : (
          <></>
        )}
      </ScrollArea>
    </Stack>
  );
}
